import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, CircleIcon, SearchIcon, ShieldCheckIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Select } from '../../components/ui/Field';
import { EmptyState } from '../../components/ui/EmptyState';
import { ServiceCard } from '../../components/services/ServiceCard';
import { ServiceDetail } from '../../components/services/ServiceDetail';
import { RequestDetail } from '../../components/services/RequestDetail';
import { ReferencePanel } from '../../components/services/ReferencePanel';
import { useAccount } from '../../contexts/AccountContext';
import { academicServices, applicationReadiness, serviceInstitutions, serviceRequests as seedRequests } from '../../data/services';
import { isOpen, searchServices, statusTone } from '../../utils/services';
import type { FulfilmentMode, ServiceCategory, ServiceRequest } from '../../types/services';
const TABS = ['Overview', 'Find a service', 'My requests', 'References', 'History'] as const;
type Tab = (typeof TABS)[number];
const CATEGORIES: (ServiceCategory | 'All categories')[] = ['All categories', 'Transcripts & Records', 'Certificates & Verification', 'References & Recommendations', 'Letters & Certifications', 'Document Assistance', 'Collection & Delivery', 'University Administrative Services', 'Other Academic Services'];
export function Services() {
  const {
    institutionName,
    campusName
  } = useAccount();
  const [tab, setTab] = useState<Tab>('Overview');
  const [institutionId, setInstitutionId] = useState('unilag');
  const [category, setCategory] = useState<ServiceCategory | 'All categories'>('All categories');
  const [query, setQuery] = useState('');
  const [openServiceId, setOpenServiceId] = useState<string | null>(null);
  const [openRequestId, setOpenRequestId] = useState<string | null>(null);
  const [requests, setRequests] = useState<ServiceRequest[]>(seedRequests);
  const institution = serviceInstitutions.find(item => item.id === institutionId)!;
  const catalogue = useMemo(() => {
    const forInstitution = academicServices.filter(service => service.institutionId === institutionId);
    const byCategory = category === 'All categories' ? forInstitution : forInstitution.filter(service => service.category === category);
    return searchServices(byCategory, query);
  }, [institutionId, category, query]);
  const popular = academicServices.filter(service => service.institutionId === institutionId && service.popular);
  const openRequests = requests.filter(request => isOpen(request.status));
  const closedRequests = requests.filter(request => !isOpen(request.status));
  const openService = academicServices.find(service => service.id === openServiceId) ?? null;
  const openRequest = requests.find(request => request.id === openRequestId) ?? null;
  function startRequest(mode: FulfilmentMode, providerId: string | null) {
    if (!openService) return;
    const id = `CR-2026-${String(300 + requests.length)}`;
    const record: ServiceRequest = {
      id,
      serviceId: openService.id,
      institutionId: openService.institutionId,
      purpose: 'Not yet stated',
      deadline: null,
      mode,
      providerId,
      status: mode === 'assisted' ? 'Awaiting provider' : 'Submitted',
      createdOn: '14 Aug 2026',
      fees: openService.fees.filter(fee => providerId ? true : fee.payee !== 'provider'),
      paymentState: mode === 'assisted' ? 'awaiting' : 'not required',
      timeline: [{
        id: 't1',
        label: 'Request created',
        at: '14 Aug 2026, 23:20',
        actor: 'You',
        state: 'done'
      }, ...(providerId ? [{
        id: 't2',
        label: 'Provider selected',
        at: '14 Aug 2026, 23:20',
        actor: 'You',
        state: 'done' as const
      }, {
        id: 't3',
        label: 'Awaiting provider acceptance',
        at: null,
        actor: 'Provider',
        state: 'current' as const
      }] : [{
        id: 't2',
        label: 'Lodge it on the official channel',
        at: null,
        actor: 'You',
        note: openService.officialMethod ?? undefined,
        state: 'current' as const
      }]), {
        id: 't9',
        label: 'Completed',
        at: null,
        actor: 'You',
        state: 'upcoming'
      }],
      documents: openService.requirements.map((requirement, index) => ({
        id: `d${index}`,
        label: requirement.label,
        state: 'required' as const
      })),
      messages: [{
        id: 'm1',
        author: 'System',
        role: 'System',
        body: mode === 'official_digital' ? 'Tracked as an official service. Campuzon holds no part of this process.' : 'Request created. The provider must accept before anything begins.',
        at: '14 Aug 2026, 23:20'
      }],
      authorisationId: null,
      shareScope: [{
        key: 'name',
        label: 'Name and matriculation number',
        on: true
      }, {
        key: 'programme',
        label: 'Programme and year',
        on: true
      }, {
        key: 'cv',
        label: 'CV',
        on: false
      }, {
        key: 'notes',
        label: 'Private Notes',
        on: false,
        locked: true,
        lockedReason: 'Notes are never shared with a provider, referee or institution.'
      }, {
        key: 'diary',
        label: 'Personal Diary',
        on: false,
        locked: true,
        lockedReason: 'Diary entries are visible only to you.'
      }],
      noteIds: []
    };
    setRequests(current => [record, ...current]);
    setOpenServiceId(null);
    setOpenRequestId(id);
    setTab('My requests');
  }
  function updateRequest(id: string, patch: Partial<ServiceRequest>) {
    setRequests(current => current.map(request => request.id === id ? {
      ...request,
      ...patch
    } : request));
  }
  return <div className="space-y-6" data-id="element-11975" data-mp="2w76sdudyafw2">
      <PageHeader eyebrow="Student services" title="Academic Services" description="Transcripts, verification, letters, references, collection and delivery. Campuzon coordinates these processes — the university still issues every document." actions={<Badge tone="ok" icon={<ShieldCheckIcon className="h-3.5 w-3.5" data-id="element-11978" data-mp="30yyki9cr8tob" />} data-id="element-11977" data-mp="30zhriv0v9xnk">
            Campuzon never asks for your university password
          </Badge>} data-id="element-11976" data-mp="2w6f7bdhzc0k1" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Academic services sections" data-id="element-11979" data-mp="39zen7globws4" />

      {tab === 'Overview' && <div className="grid gap-4 lg:grid-cols-3" data-id="element-11980" data-mp="3tlyave8bglfv">
          <div className="space-y-4 lg:col-span-2" data-id="element-11981" data-mp="3tj3srtcrzcpm">
            <Panel title="What do you need help with?" description="Search by document, letter or process." data-id="element-11982" data-mp="2dapiya2sy8b">
              <label className="relative block" data-id="element-11983" data-mp="3oltt7kvo9ko2">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" data-id="element-11984" data-mp="3okinvgmkw4uu" />
                <input value={query} onChange={event => setQuery(event.target.value)} onKeyDown={event => {
              if (event.key === 'Enter') setTab('Find a service');
            }} placeholder="Transcript, English proficiency letter, certificate verification…" aria-label="Search academic services" className="h-11 w-full rounded-lg border border-line-strong bg-surface pl-9 pr-3 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-700 focus:outline-none" data-id="element-11985" data-mp="3asc8hwr921wg" />
              </label>
              {query.trim().length > 0 && <Button size="sm" variant="secondary" className="mt-2.5" icon={<ArrowRightIcon className="h-3.5 w-3.5" data-id="element-11987" data-mp="2ycwej7m87aot" />} onClick={() => setTab('Find a service')} data-id="element-11986" data-mp="37e41wun7er44">
                  See matches at {institution.short}
                </Button>}

              <p className="mt-4 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-11988" data-mp="2omt8o6u0a292">
                Commonly needed at {institution.short}
              </p>
              <ul className="mt-2 grid gap-2 sm:grid-cols-2" data-id="element-11989" data-mp="2ep6szi4vu6o1">
                {popular.map(service => <li key={service.id} data-id="element-11990" data-mp="2antlz4q1zxfj">
                    <button type="button" onClick={() => {
                setOpenServiceId(service.id);
                setTab('Find a service');
              }} className="w-full rounded-lg border border-line-strong bg-surface px-3.5 py-2.5 text-left text-[13px] font-medium text-ink transition-colors duration-150 ease-out hover:border-ink-subtle" data-id="element-11991" data-mp="2amsax091faxt">
                      {service.name}
                      <span className="mt-0.5 block text-[12px] font-normal text-ink-muted" data-id="element-11992" data-mp="z5fm0y86ie1i">
                        {service.category}
                      </span>
                    </button>
                  </li>)}
              </ul>
            </Panel>

            <Panel title="Requests in flight" description="Everything still waiting on you, a provider or an institution." action={<Button size="sm" variant="ghost" onClick={() => setTab('My requests')} data-id="element-11994" data-mp="3exd7ecsla5nw">
                  See all
                </Button>} data-id="element-11993" data-mp="bg4r3xdifdva">
              {openRequests.length === 0 ? <EmptyState title="Nothing in progress" description="Start a service request and it will appear here with its deadline and next step." data-id="element-11995" data-mp="1iodi0vbn8dc1" /> : <ul className="divide-y divide-line" data-id="element-11996" data-mp="1rpto4qvfdtag">
                  {openRequests.map(request => {
              const service = academicServices.find(item => item.id === request.serviceId)!;
              return <li key={request.id} data-id="element-11997" data-mp="yy0an76r3lwy">
                        <button type="button" onClick={() => {
                  setOpenRequestId(request.id);
                  setTab('My requests');
                }} className="flex w-full flex-wrap items-center gap-2 py-3 text-left first:pt-0" data-id="element-11998" data-mp="13zze0h2hqitw">
                          <span className="min-w-0 flex-1" data-id="element-11999" data-mp="l7wrip3648bq">
                            <span className="block text-[13px] font-medium text-ink" data-id="element-12000" data-mp="l6db8yhihx9y">{service.name}</span>
                            <span className="block text-[12px] text-ink-muted" data-id="element-12001" data-mp="dmkugigzesl4">
                              {request.purpose} ·{' '}
                              {request.deadline ? `due ${request.deadline}` : 'no deadline'}
                            </span>
                          </span>
                          <Badge tone={statusTone(request.status)} data-id="element-12002" data-mp="3uzewmw6zzo1x">{request.status}</Badge>
                        </button>
                      </li>;
            })}
                </ul>}
            </Panel>
          </div>

          <div className="space-y-4" data-id="element-12003" data-mp="3h6fiaxiybzvy">
            <Panel title="Application readiness" description={applicationReadiness.label} data-id="element-12004" data-mp="3h3b6bdz8vzuq">
              <ul className="space-y-2" data-id="element-12005" data-mp="377zogmsjicir">
                {applicationReadiness.items.map(item => <li key={item.label} className="flex items-start gap-2" data-id="element-12006" data-mp="335cx0mzs6tm3">
                    {item.done ? <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-ok-700" data-id="element-12007" data-mp="2sjpx533cyui7" /> : <CircleIcon className="mt-1 h-3 w-3 shrink-0 text-ink-subtle" data-id="element-12008" data-mp="2xknfbfodbojp" />}
                    <span className="min-w-0" data-id="element-12009" data-mp="2jlnpw5t175mh">
                      <span className={`block text-[13px] ${item.done ? 'text-ink' : 'font-medium text-ink'}`} data-id="element-12010" data-mp="2oiw9mattvhpu">
                        {item.label}
                      </span>
                      <span className="block text-[12px] leading-5 text-ink-muted" data-id="element-12011" data-mp="3cauwls2n31it">{item.via}</span>
                    </span>
                  </li>)}
              </ul>
              <p className="mt-3 border-t border-line pt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-12012" data-mp="32ioxzzhvtlun">
                Deadline {applicationReadiness.deadline}. Three items depend on other people, so start
                them earliest.
              </p>
              <Link to="/discover" className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-800 hover:underline" data-id="element-12013" data-mp="3tgpfyp46r96">
                Open the application workspace <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-12014" data-mp="953ubmgj83sg" />
              </Link>
            </Panel>

            <Panel title="Which institution?" data-id="element-12015" data-mp="mwih84dk8p4s">
              <Select value={institutionId} onChange={event => {
            setInstitutionId(event.target.value);
            setOpenServiceId(null);
          }} aria-label="Institution" data-id="element-12016" data-mp="mzeedqhl39s6">
                {serviceInstitutions.map(item => <option key={item.id} value={item.id} data-id="element-12017" data-mp="47lnc1m2ryn3">
                    {item.name}
                    {item.pilot ? ' (pilot)' : ''}
                  </option>)}
              </Select>
              <p className="mt-2 text-[12px] leading-5 text-ink-muted" data-id="element-12018" data-mp="1flfzw8loony2">
                Your account is registered at {institutionName} · {campusName}. Each institution publishes its own
                catalogue, so services and fees differ.
              </p>
            </Panel>
          </div>
        </div>}

      {tab === 'Find a service' && <>
          {openService ? <ServiceDetail service={openService} onBack={() => setOpenServiceId(null)} onStart={startRequest} data-id="element-12019" data-mp="3q9csfkzex0i5" /> : <div className="space-y-4" data-id="element-12020" data-mp="356bnwyynk0s">
              <Panel title={`${institution.name} service catalogue`} data-id="element-12021" data-mp="343o9z0ujzpm">
                <div className="flex flex-wrap gap-3" data-id="element-12022" data-mp="83g625cv2glc">
                  <label className="relative min-w-[220px] flex-1" data-id="element-12023" data-mp="81wq6fv4a7k3">
                    <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" data-id="element-12024" data-mp="cw0yb2g4clmr" />
                    <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search this catalogue" aria-label="Search catalogue" className="h-10 w-full rounded-lg border border-line-strong bg-surface pl-9 pr-3 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-700 focus:outline-none" data-id="element-12025" data-mp="hvvof3s2l76r" />
                  </label>
                  <Select value={institutionId} onChange={event => setInstitutionId(event.target.value)} aria-label="Institution" className="w-auto" data-id="element-12026" data-mp="22c3luojg3q9j">
                    {serviceInstitutions.map(item => <option key={item.id} value={item.id} data-id="element-12027" data-mp="2q3irorafkppj">
                        {item.short}
                      </option>)}
                  </Select>
                  <Select value={category} onChange={event => setCategory(event.target.value as ServiceCategory | 'All categories')} aria-label="Category" className="w-auto" data-id="element-12028" data-mp="2gb3ahw6yk55p">
                    {CATEGORIES.map(item => <option key={item} data-id="element-12029" data-mp="qhc6sbof0xc">{item}</option>)}
                  </Select>
                </div>
                <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-12030" data-mp="3j3ffn97ogbw9">
                  {catalogue.length} service{catalogue.length === 1 ? '' : 's'} listed. Where an
                  institution has not published a fee or timeline, Campuzon says so rather than
                  estimating.
                </p>
              </Panel>

              {catalogue.length === 0 ? <EmptyState title="No services match" description="Try a different category, or clear the search to see the whole catalogue for this institution." data-id="element-12031" data-mp="306ea9na7o9rs" /> : <ul className="space-y-2.5" data-id="element-12032" data-mp="ybvhtnf9j824">
                  {catalogue.map(service => <li key={service.id} data-id="element-12033" data-mp="12es3xo40hmat">
                      <ServiceCard service={service} onOpen={() => setOpenServiceId(service.id)} data-id="element-12034" data-mp="2mx41rgu2um94" />
                    </li>)}
                </ul>}
            </div>}
        </>}

      {tab === 'My requests' && <>
          {openRequest ? <RequestDetail request={openRequest} onBack={() => setOpenRequestId(null)} onUpdate={patch => updateRequest(openRequest.id, patch)} data-id="element-12035" data-mp="2rw6wa7cfla2u" /> : openRequests.length === 0 ? <EmptyState title="No open requests" description="Find a service to get started. Completed requests stay in your history." action={<Button variant="primary" onClick={() => setTab('Find a service')} data-id="element-12037" data-mp="3anq02gea4s5x">
                  Find a service
                </Button>} data-id="element-12036" data-mp="31m8mb4och2ke" /> : <ul className="space-y-2.5" data-id="element-12038" data-mp="1ga8hl7sqelhr">
              {openRequests.map(request => {
          const service = academicServices.find(item => item.id === request.serviceId)!;
          const next = request.timeline.find(entry => entry.state === 'current');
          return <li key={request.id} data-id="element-12039" data-mp="14749krt6anhe">
                    <button type="button" onClick={() => setOpenRequestId(request.id)} className="w-full rounded-card border border-line bg-surface px-4 py-3.5 text-left transition-colors duration-150 ease-out hover:border-ink-subtle" data-id="element-12040" data-mp="1485m70pu7i18">
                      <span className="flex flex-wrap items-center gap-2" data-id="element-12041" data-mp="les96am2xw9w">
                        <span className="text-[14px] font-semibold text-ink" data-id="element-12042" data-mp="lho6bwq3sgxa">{service.name}</span>
                        <Badge tone={statusTone(request.status)} data-id="element-12043" data-mp="qc1x1lttlzvm">{request.status}</Badge>
                        <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-12044" data-mp="1wwkxbc5ulg7a">{request.id}</span>
                      </span>
                      <span className="mt-1 block text-[13px] text-ink-muted" data-id="element-12045" data-mp="3mbcdkogisuy7">{request.purpose}</span>
                      <span className="mt-1 block text-[12px] text-ink-subtle" data-id="element-12046" data-mp="3hbzixhjeqjl2">
                        {next ? `Now: ${next.label} · ${next.actor}` : 'No action pending'}
                        {request.deadline ? ` · due ${request.deadline}` : ''}
                      </span>
                    </button>
                  </li>;
        })}
            </ul>}
        </>}

      {tab === 'References' && <ReferencePanel data-id="element-12047" data-mp="2yl8e8tad7hd0" />}

      {tab === 'History' && <Panel title="Academic services history" description="A permanent record of everything you have requested, with its status trail." data-id="element-12048" data-mp="2tmoo0goqnjeu">
          {closedRequests.length === 0 ? <EmptyState title="Nothing completed yet" description="Finished requests keep their documents, payments, messages and verification record here." data-id="element-12049" data-mp="rvtrfmmpjg72" /> : <ul className="divide-y divide-line" data-id="element-12050" data-mp="1g60enf1marqn">
              {closedRequests.map(request => {
          const service = academicServices.find(item => item.id === request.serviceId)!;
          const finished = request.timeline.filter(entry => entry.state === 'done').length;
          return <li key={request.id} data-id="element-12051" data-mp="12fes9gcyfzo0">
                    <button type="button" onClick={() => {
              setOpenRequestId(request.id);
              setTab('My requests');
            }} className="flex w-full flex-wrap items-center gap-2 py-3 text-left first:pt-0" data-id="element-12052" data-mp="xgalsxhf0g1d">
                      <span className="min-w-0 flex-1" data-id="element-12053" data-mp="jidnnwnbc1kl">
                        <span className="block text-[13px] font-medium text-ink" data-id="element-12054" data-mp="2hwxeqcoo3wne">{service.name}</span>
                        <span className="block text-[12px] text-ink-muted" data-id="element-12055" data-mp="2duak640m3xmi">
                          {request.purpose} · created {request.createdOn} · {finished} recorded steps
                        </span>
                      </span>
                      {request.verification && <Badge tone="ok" data-id="element-12056" data-mp="4k1fagcc9pt0">Verifiable</Badge>}
                      <Badge tone={statusTone(request.status)} data-id="element-12057" data-mp="9dxa59xb2wjy">{request.status}</Badge>
                    </button>
                  </li>;
        })}
            </ul>}
        </Panel>}
    </div>;
}